import { nip19 } from "nostr-tools";
import { useGraspServerInfo } from "@/hooks/useGraspServerInfo";
import { useLoadProfile } from "@/hooks/useLoadProfile";
import { useProfile } from "@/hooks/useProfile";
import { useVerifiedNip05 } from "@/hooks/useVerifiedNip05";
import { decodePubkeyIdentifier, standardizeNip05 } from "@/lib/routeUtils";

function nip05Domain(nip05: string | undefined): string | undefined {
  const separator = nip05?.lastIndexOf("@") ?? -1;
  return separator >= 0 ? nip05?.slice(separator + 1).toLowerCase() : undefined;
}

export interface CoordinatorServiceIdentity {
  pubkey: string;
  npub: string;
  /** Trust-bearing label: verified NIP-05, never a self-asserted profile name. */
  label: string;
  rawNip05: string | undefined;
  verifiedNip05: string | undefined;
  domain: string | undefined;
  nip05Verified: boolean;
  isGraspServer: boolean;
  nip11OperatorMatches: boolean;
  linkedGraspService: boolean;
  server: ReturnType<typeof useGraspServerInfo>;
}

/** Resolve the two-way NIP-05 + NIP-11 relationship for a coordinator key. */
export function useCoordinatorServiceIdentity(
  pubkey: string | undefined,
): CoordinatorServiceIdentity | undefined {
  useLoadProfile(pubkey);
  const profile = useProfile(pubkey);
  const verifiedNip05 = useVerifiedNip05(pubkey);
  const rawNip05 = profile?.nip05 ? standardizeNip05(profile.nip05) : undefined;
  const domain = nip05Domain(verifiedNip05);
  const server = useGraspServerInfo(domain);
  const serverPubkey =
    server?.status === "found" && server.document.pubkey
      ? decodePubkeyIdentifier(server.document.pubkey)
      : undefined;
  const isGraspServer =
    server?.status === "found" &&
    (server.document.supported_grasps?.length ?? 0) > 0;
  const nip11OperatorMatches = !!pubkey && serverPubkey === pubkey;

  if (!pubkey) return undefined;

  const npub = nip19.npubEncode(pubkey);
  return {
    pubkey,
    npub,
    label: verifiedNip05 ?? `${npub.slice(0, 16)}…`,
    rawNip05,
    verifiedNip05,
    domain,
    nip05Verified: verifiedNip05 !== undefined,
    isGraspServer,
    nip11OperatorMatches,
    linkedGraspService: isGraspServer && nip11OperatorMatches,
    server,
  };
}
